// Las llamadas al servidor del widget.
//
// En la home todo esto lo resuelve `NTv3.gr()`, que construye las URLs a partir de los
// globales `NT.vars.lang` y `NT.vars.prefixAlias`. Fuera de renfeviajes no existe ninguno de
// los dos, así que el widget construye URLs ABSOLUTAS contra su propio origen.
//
// Dos cosas que hoy viajan en la sesión y aquí tienen que ir explícitas (es el bloqueante de
// F4, y por eso se pasan ya desde ahora aunque el servidor todavía las ignore):
//   - la MARCA (`nivelcarteraid`): las consultas de estaciones filtran por marca, y el `id` de
//     destino y la `url` de landing que devuelven dependen de ella;
//   - el IDIOMA: también filtra, y además decide el prefijo de la ruta.
//
// Las peticiones van con `credentials: 'omit'`: el widget no necesita la sesión del cliente y
// no tiene por qué mandar cookies de renfeviajes desde la página de un tercero.

export const ORIGEN_POR_DEFECTO = 'https://renfeviajes.renfe.com'

export class Datos {
  /**
   * @param {object} opciones
   * @param {string} [opciones.origen]   de dónde cuelgan las llamadas y las URLs de salida
   * @param {string} [opciones.locale]   es | en | fr
   * @param {string|number} [opciones.marca]  id de agente / nivel de cartera
   */
  constructor (opciones = {}) {
    this.origen = (opciones.origen || ORIGEN_POR_DEFECTO).replace(/\/+$/, '')
    this.locale = opciones.locale || 'es'
    this.marca = opciones.marca || ''
  }

  /**
   * La URL de una ruta del sitio, absoluta y con el idioma delante. Sustituye a NTv3.gr().
   * @param {string} ruta  p. ej. 'Search/stationsRenfe' o 'Compra/3833'
   * @param {object} [parametros]
   */
  url (ruta, parametros = null) {
    const u = new URL(this.origen + '/' + this.locale + '/' + String(ruta).replace(/^\/+/, ''))
    if (parametros) {
      for (const [k, v] of Object.entries(parametros)) {
        if (v !== undefined && v !== null && v !== '') u.searchParams.set(k, v)
      }
    }
    return u.toString()
  }

  async pedir (ruta, parametros, señal) {
    const marcados = Object.assign({}, parametros)
    // La MARCA va explícita porque fuera de renfeviajes no hay sesión de la que sacarla, y de
    // ella dependen las estaciones que se devuelven y la url de landing del destino.
    //
    // El IDIOMA no: ya viaja en la ruta (`/es/Search/...`, ver url()), que es de donde lo lee
    // el router. Mandarlo además por query sería ruido.
    if (this.marca) marcados.marca = this.marca

    const respuesta = await fetch(this.url(ruta, marcados), {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      signal: señal,
    })
    if (!respuesta.ok) throw new Error(ruta + ' respondió ' + respuesta.status)
    const texto = await respuesta.text()
    try {
      return JSON.parse(texto)
    } catch (e) {
      // Cuando la sesión caduca o el WAF se mete por medio, esto devuelve HTML y no JSON.
      // Mejor un error con nombre que un "undefined is not a function" tres funciones más allá.
      throw new Error(ruta + ' no ha devuelto JSON')
    }
  }

  /**
   * Estaciones de tren, para los campos de origen y destino.
   * @param {string} [tipo]  `trenCoche` para el destino del buscador de tren + coche: sólo las
   *                         estaciones con oficina de alquiler. Sin él, todas.
   */
  estaciones (q, señal, tipo = '') {
    return this.pedir('Search/stationsRenfe', { q: q || '', type: tipo || '' }, señal)
  }

  /**
   * EL CATÁLOGO ENTERO de estaciones, de una vez. Es lo que permite que el autocompletado no
   * consulte al servidor en cada pulsación: `estaciones()` filtra por nombre y devuelve como
   * mucho 20 filas, así que con ella no se puede reconstruir el catálogo.
   *
   * Trae un campo de más, `isfav`, que es por lo que ordena el servidor antes que por el
   * parecido del nombre.
   *
   * Con `tipo` (`trenCoche`), el catálogo del destino del buscador de tren + coche.
   */
  estacionesTodas (señal, tipo = '') {
    return this.pedir('Search/stationsRenfeAll', tipo ? { type: tipo } : {}, señal)
  }

  /** Destinos genéricos (la pestaña de ocio los usa para resolver por nombre). */
  destinos (q, señal) {
    return this.pedir('Search/destinations', { q: q || '' }, señal)
  }

  /** Destinos de circuitos. */
  circuitos (q, señal) {
    return this.pedir('Search/destinationsRenfe', { q: q || '', type: 'circuitos' }, señal)
  }

  /** Productos de ocio. */
  productos (q, señal) {
    return this.pedir('Search/products', { q: q || '' }, señal)
  }

  /** Ciudades y hoteles, para la variante con hotel. */
  ciudadesYHoteles (q, señal) {
    return this.pedir('RESTprocesoCompraUnificado/getAllSpanishCitiesHotels', { q: q || '' }, señal)
  }

  /**
   * El paquete Viaje+ de una estación de destino. Es lo que decide si la salida va por
   * CompraDestino/{idPaquete} o por el fallback con parámetros `_param…`.
   * Devuelve null si no hay paquete o si la llamada falla: en la home, cualquiera de las dos
   * cosas cae al mismo fallback.
   */
  async viajePlus (idEstacionDestino, señal) {
    try {
      return await this.viajePlusCrudo(idEstacionDestino, señal)
    } catch (e) {
      return null
    }
  }

  /**
   * Lo mismo pero SIN tragarse el error, porque la rama de salida depende de distinguir dos
   * cosas que no son iguales: que la llamada FALLE (entonces la home cae a la forma _param…)
   * y que responda `null` (entonces NO cae: sigue por CompraDestino, con el id sin resolver).
   */
  viajePlusCrudo (idEstacionDestino, señal) {
    return this.pedir(
      'RESTviajePlus/getViajePlusByEstacionIdExclusivo/' + encodeURIComponent(idEstacionDestino) + '/false',
      {}, señal)
  }

  /**
   * El catálogo de ocio de la pestaña Ocio. Ojo: no es un autocompletado por AJAX como los
   * demás — se descarga ENTERO de una vez al abrir la pestaña y se filtra en el cliente.
   */
  catalogoOcio (señal) {
    // La ruta lleva los argumentos en el path, no en la query: false / [ids de vocabulario] / idioma.
    return this.pedir(
      'Productos/getByVocabularioFilterByTagInJson/false/' + encodeURIComponent('["1214"]') + '/' + this.locale,
      {}, señal)
  }
}
